import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { FlexRow } from 'src/components/Reusable/Flex'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import { BasicDropdownProvider } from 'src/components/Reusable/BasicDropdown/context/BasicDropdownContext'
import { Control } from '@trustero/trustero-api-web/lib/model/control_pb'
import { NoResults } from 'src/components/PageLayout/NoResults'
import { NoControlsIcon } from 'src/components/Icons/EmptyStateIcons'
import { useUser } from 'src/components/async/Users'
import { ControlsAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { USER_ROLE } from '@trustero/trustero-api-web/lib/account/account_pb'
import { useAudits } from 'src/components/async/model/audit/useAudit'
import { GatekeeperFlag, IsGatekeeperEnabled } from 'src/context/Gatekeeper'
import { useIsContentSearchActive } from 'src/Utils/search/search.hooks'
import { Grid } from '../../../../components/Reusable/Grid'
import { ControlResponsibilityModal } from '../../modals/ControlResponsibilityModal'
import { ControlListItemConfig } from '../../IndexItem/ControlListItem.constants'
import { getControlsListItemConfig } from '../../IndexItem/ControlsListItem.helpers'
import { NoControlsButtonGroup } from '../NoControlsButtonGroup'
import { ControlsIndexGridHeader } from './ControlsIndexGridHeader'
import {
  ControlGridRows,
  getControlsGridEmptyState,
} from './ControlsIndexGrid.helpers'

interface ControlsIndexGridProps {
  controls: Control[]
  hasNoControls: boolean
  isLoading?: boolean
  audit?: AuditRecord
  config?: ControlListItemConfig
}

export const ControlsIndexGrid = ({
  audit,
  controls,
  isLoading,
  hasNoControls,
  config,
}: ControlsIndexGridProps): JSX.Element => {
  const location = useLocation()
  const { data: user } = useUser()
  const isContentSearchActive = useIsContentSearchActive()
  const isMultiPolicyEnabled = IsGatekeeperEnabled(
    GatekeeperFlag.MULTI_POLICY_CONTROL_LINKAGES,
  )
  const policyNamesByControlId = useMemo(() => {
    const map: Record<string, string[]> = {}
    if (!isMultiPolicyEnabled) return map
    controls.forEach((control) => {
      map[control.getId()] = control
        .getLinkedPoliciesList()
        .map((policy) => policy.getPolicyName())
        .filter(Boolean)
    })
    return map
  }, [controls, isMultiPolicyEnabled])
  const departmentNamesByControlId = useMemo(() => {
    const map: Record<string, string[]> = {}
    if (!isMultiPolicyEnabled) return map
    controls.forEach((control) => {
      const uniqueDepartmentNames = new Set(
        control
          .getLinkedPoliciesList()
          .map((policy) => policy.getDepartment())
          .filter(Boolean),
      )
      map[control.getId()] = Array.from(uniqueDepartmentNames)
    })
    return map
  }, [controls, isMultiPolicyEnabled])
  const isAdmin: boolean = user?.getRole() === USER_ROLE.ADMIN || false
  const audits = useAudits()
  const hasOpenAudits = useMemo<boolean>(() => {
    return (audits.data?.getItemsList() || []).some(
      (auditItem: AuditRecord) => !auditItem.getIsClosed(),
    )
  }, [audits])

  if (isLoading) {
    return (
      <FlexRow $mt={40}>
        <TableListLoader rows={20} />
      </FlexRow>
    )
  }

  if (hasNoControls) {
    return (
      <NoResults
        icons={<NoControlsIcon />}
        title="You have no controls yet."
        note="Use a starter set specific to a compliance framework, upload a set of controls if you already have them, or add controls manually."
        buttons={
          <NoControlsButtonGroup
            isAdmin={isAdmin}
            hasOpenAudits={hasOpenAudits}
          />
        }
      />
    )
  }

  const controlGridEmptyState = getControlsGridEmptyState()

  const { template } = getControlsListItemConfig(config)

  const isControlsIndex = location.pathname.includes(
    ControlsAbsoluteRoutes.INDEX,
  )
  const showContentMatchColumn = isContentSearchActive && isControlsIndex

  const templateColumns = showContentMatchColumn
    ? `min-content ${template}`
    : template

  return !controls?.length ? (
    <>{controlGridEmptyState}</>
  ) : (
    <BasicDropdownProvider>
      <Grid pb="xs" gridTemplateColumns={templateColumns}>
        <ControlsIndexGridHeader
          config={config}
          showContentMatchColumn={showContentMatchColumn}
        />
        <ControlGridRows
          controls={controls}
          audit={audit}
          config={config}
          policyNamesByControlId={policyNamesByControlId}
          departmentNamesByControlId={departmentNamesByControlId}
        />
      </Grid>
      {/* Modals */}
      <ControlResponsibilityModal />
    </BasicDropdownProvider>
  )
}
